import { CheckOutlined } from "@ant-design/icons";
import { captureException } from "@sentry/nextjs";
import { Button, Card, Col, Divider, Row, Typography } from "antd";
import classNames from "classnames";
import { useState } from "react";
import { FormattedMessage } from "react-intl";

import { formatMoneyAmount } from "../../lib/formats/money";
import { useCurrentLocale } from "../../lib/hooks/basic/locales";
import { useSubscriptionCreate } from "../../lib/hooks/basic/tenants";
import { useCurrentUser } from "../../lib/hooks/basic/users";
import redirect, { hardRedirect } from "../../lib/redirect";
import routes from "../../lib/routes";
import makePlans from "./pricing";
import en from "./pricing.en";
import es from "./pricing.es";

import styles from "./styles.module.css";

const locales = { en, es };

const SUPPORT_MAILTO = "mailto:hola@aragro.com";

const tryOpenHubspotChat = () => {
  if (typeof window === "undefined") return false;
  const hubspot = window.HubSpotConversations;
  return hubspot?.widget?.open ? (hubspot.widget.open(), true) : false;
};

interface PriceDisplayProps {
  plan: any;
  isAnnual: boolean;
  l: any;
}

const formatPrice = (amount: number) =>
  formatMoneyAmount(amount, "USD", { maximumFractionDigits: 0 });

const PriceDisplay: React.FC<PriceDisplayProps> = ({ plan, isAnnual, l }) => {
  if (typeof plan.price === "string") {
    return (
      <div className={styles.priceContainer}>
        <div className={styles.amount} style={{ fontSize: "1.5rem" }}>
          {l[plan.name].price}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.priceContainer}>
      {isAnnual && plan.price.year ? (
        <>
          <div className={styles.priceRow}>
            <div>
              <span className={styles.currency}>US</span>
              <span className={styles.amount}>
                {formatPrice(plan.price.year)}
              </span>
              <span className={styles.period}>
                / {l.per_month_short || "mo"}
              </span>
            </div>
          </div>
          <div className={styles.crossedPrice}>
            <span className={styles.currency}>US</span>
            <span className={styles.amount}>
              {formatPrice(plan.price.month)}
            </span>
            <span className={styles.period}>/ {l.per_month_short || "mo"}</span>
          </div>
          <Typography.Text className={styles.annualEquivalent}>
            {l.annual_equivalent}
          </Typography.Text>
          <Typography.Text className={styles.annualTotal}>
            <strong>US{formatPrice(plan.price.year * 12)}</strong>{" "}
            {l.annual_total}
          </Typography.Text>
        </>
      ) : (
        <div className={styles.priceRow}>
          <div>
            <span className={styles.currency}>US</span>
            <span className={styles.amount}>
              {formatPrice(plan.price.month)}
            </span>
            <span className={styles.period}>/ {l.per_month_short || "mo"}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export function SubscriptionPlans() {
  const plans = makePlans();
  const { user, currentTenant } = useCurrentUser();
  const { currentLocale } = useCurrentLocale();
  const [period, setPeriod] = useState("month");
  const [generateLink, { loading }] = useSubscriptionCreate();

  if (!plans) return null;

  const isAnnual = period === "year";
  const l: any = locales[currentLocale].pricing;

  const onContactEnterprise: React.MouseEventHandler<HTMLAnchorElement> = (
    event
  ) => {
    if (tryOpenHubspotChat()) event.preventDefault();
  };

  const onPressPlan = (plan?: string) => {
    generateLink({ variables: { plan, period } })
      .then(({ data }) => {
        if (data?.subscriptionCreate?.link) {
          redirect(data.subscriptionCreate.link);
        } else {
          hardRedirect(routes.onboarding + "?pillars=true");
        }
      })
      .catch((e) => {
        captureException(e);
      });
  };

  const isEnterprise = currentTenant.planName === "enterprise";

  return (
    <div>
      {!isEnterprise && (
        <div className={styles.toggleWrapper}>
          <div className={styles.toggleContainer}>
            <div
              className={styles.toggleHighlight}
              style={{
                transform: `translateX(${isAnnual ? "100%" : "0%"})`,
                width: "50%",
              }}
            />
            <button
              className={classNames(styles.toggleBtn, {
                [styles.toggleBtnActive]: !isAnnual,
              })}
              onClick={() => setPeriod("month")}
            >
              {l.monthly}
            </button>
            <button
              className={classNames(styles.toggleBtn, {
                [styles.toggleBtnActive]: isAnnual,
              })}
              onClick={() => setPeriod("year")}
            >
              {l.annual}
              <span className={styles.discountText}>{l.discount}</span>
            </button>
          </div>
        </div>
      )}

      <Row gutter={[24, 24]} justify="center">
        {plans.map((plan) => {
          const isCurrent =
            currentTenant.subscriptionStatus === "active" &&
            currentTenant.planName === plan.name;

          return (
            <Col key={plan.name} xs={24} lg={8}>
              <Card
                className={classNames(styles.card, {
                  [styles.cardFeatured]:
                    plan.featured && currentTenant.planName === "free",
                  [styles.cardCurrent]: isCurrent,
                })}
              >
                {plan.featured && currentTenant.planName === "free" && (
                  <div className={styles.featuredBadge}>{l.featured}</div>
                )}

                {isCurrent && (
                  <>
                    <div className={styles.currentPlanBadge}>
                      <FormattedMessage
                        id="subscriptions.currentPlan"
                        defaultMessage="Current plan"
                      />
                    </div>
                  </>
                )}

                <Typography.Title level={2}>
                  {l[plan.name].title}
                </Typography.Title>

                <Typography.Title level={4}>
                  {l[plan.name].subtitle}
                </Typography.Title>

                <Typography.Paragraph>
                  {l[plan.name].description}
                </Typography.Paragraph>

                <>
                  <PriceDisplay plan={plan} isAnnual={isAnnual} l={l} />

                  <Button
                    className={styles.ctaBtn}
                    type="primary"
                    size="large"
                    onClick={() => onPressPlan(plan.name)}
                    disabled={loading || isCurrent}
                    block
                  >
                    {isCurrent ? (
                      <FormattedMessage
                        id="subscriptions.currentPlan"
                        defaultMessage="Current plan"
                      />
                    ) : (
                      l.cta[plan.cta]
                    )}
                  </Button>
                </>

                <Typography.Title level={5}>
                  {l[plan.name].includes}
                </Typography.Title>

                <ul className={styles.featureList}>
                  {plan.features.map((feature) => (
                    <li key={feature} className={styles.featureItem}>
                      <CheckOutlined className={styles.checkIcon} />
                      <span>{l[plan.name].features[feature]}</span>
                    </li>
                  ))}
                </ul>

                {plan.name === "enterprise" && (
                  <div className={styles.upsellSection}>
                    <Typography.Text strong>
                      {l.enterprise.upsell.title}
                    </Typography.Text>
                    <ul className={styles.upsellList}>
                      {(
                        [
                          l.enterprise.upsell.payroll,
                          l.enterprise.upsell.accounting,
                          l.enterprise.upsell.integrations,
                        ] as string[]
                      ).map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                    <a
                      href={SUPPORT_MAILTO}
                      onClick={onContactEnterprise}
                      className={styles.upsellCta}
                    >
                      {l.enterprise.upsell.cta} →
                    </a>
                  </div>
                )}
              </Card>
            </Col>
          );
        })}

        {!user?.tenantsCount && user?.settings.company_name && (
          <Col span={24} style={{ textAlign: "center", marginTop: "2rem" }}>
            <Divider />
            <Button
              type="primary"
              className="btn-complete"
              onClick={() => onPressPlan()}
              loading={loading}
              disabled={loading}
              size="large"
            >
              <FormattedMessage
                id="subscriptions.freePlan"
                defaultMessage="Try for free"
              />
            </Button>
          </Col>
        )}
      </Row>
    </div>
  );
}
